import type { ReactNode } from "react";
import { motion } from "motion/react";

const EASE = [0.32, 0.72, 0, 1] as const;

interface RevealProps {
  /** Animates in when true (replays when returning from the overview). */
  active: boolean;
  delay?: number;
  y?: number;
  className?: string;
  children: ReactNode;
}

/** Single fade + rise + deblur entrance. */
export function Reveal({ active, delay = 0, y = 24, className = "", children }: RevealProps) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y, filter: "blur(6px)" }}
      animate={
        active
          ? { opacity: 1, y: 0, filter: "blur(0px)" }
          : { opacity: 0, y, filter: "blur(6px)" }
      }
      transition={{ delay: active ? delay : 0, duration: 0.55, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

interface RevealListProps {
  active: boolean;
  /** Seconds between each child's entrance. */
  stagger?: number;
  delay?: number;
  className?: string;
  children: ReactNode;
}

/** Staggered container — direct children must be <RevealItem>. */
export function RevealList({
  active,
  stagger = 0.12,
  delay = 0,
  className = "",
  children,
}: RevealListProps) {
  return (
    <motion.div
      className={className}
      initial="hidden"
      animate={active ? "show" : "hidden"}
      variants={{
        hidden: {},
        show: { transition: { staggerChildren: stagger, delayChildren: delay } },
      }}
    >
      {children}
    </motion.div>
  );
}

interface RevealItemProps {
  className?: string;
  children: ReactNode;
}

export function RevealItem({ className = "", children }: RevealItemProps) {
  return (
    <motion.div
      className={className}
      variants={{
        hidden: { opacity: 0, y: 24, filter: "blur(6px)" },
        show: {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          transition: { duration: 0.55, ease: EASE },
        },
      }}
    >
      {children}
    </motion.div>
  );
}
